import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@resideo/blueprint-oneportal";
import styles from "./LocationDetailsStep.module.css";

export interface LocationDetailsStepProps {
  onBack: () => void;
  onFinish: () => void;
}

interface LocationFormValues {
  locationName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

const INITIAL_VALUES: LocationFormValues = {
  locationName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
};

/**
 * Final onboarding step: lets the user confirm/edit their first location's
 * details before finishing the tour. Not yet wired to a save API.
 */
export const LocationDetailsStep: React.FC<LocationDetailsStepProps> = ({
  onBack,
  onFinish,
}) => {
  const { t } = useTranslation();
  const [values, setValues] = useState<LocationFormValues>(INITIAL_VALUES);

  const handleChange =
    (field: keyof LocationFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setValues((prev) => ({ ...prev, [field]: value }));
    };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-details-title"
      data-testid="onboarding-location-step"
    >
      <div className={styles.content}>
        <h2 id="location-details-title" className={styles.title}>
          {t("onboardingTour.locationStep.title")}
        </h2>
        <p className={styles.subtitle}>
          {t("onboardingTour.locationStep.subtitle")}
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="onboarding-location-name">
            {t("onboardingTour.locationStep.locationName")}
          </label>
          <Input
            id="onboarding-location-name"
            name="onboarding-location-name"
            type="text"
            autoFocus
            className={styles.input}
            value={values.locationName}
            onChange={handleChange("locationName")}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="onboarding-address-1">
            {t("onboardingTour.locationStep.addressLine1")}
          </label>
          <Input
            id="onboarding-address-1"
            name="onboarding-address-1"
            type="text"
            className={styles.input}
            value={values.addressLine1}
            onChange={handleChange("addressLine1")}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="onboarding-address-2">
            {t("onboardingTour.locationStep.addressLine2")}
          </label>
          <Input
            id="onboarding-address-2"
            name="onboarding-address-2"
            type="text"
            className={styles.input}
            value={values.addressLine2}
            onChange={handleChange("addressLine2")}
          />
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="onboarding-city">
              {t("onboardingTour.locationStep.city")}
            </label>
            <Input
              id="onboarding-city"
              name="onboarding-city"
              type="text"
              className={styles.input}
              value={values.city}
              onChange={handleChange("city")}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="onboarding-state">
              {t("onboardingTour.locationStep.state")}
            </label>
            <Input
              id="onboarding-state"
              name="onboarding-state"
              type="text"
              className={styles.input}
              value={values.state}
              onChange={handleChange("state")}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="onboarding-zip">
              {t("onboardingTour.locationStep.zip")}
            </label>
            <Input
              id="onboarding-zip"
              name="onboarding-zip"
              type="text"
              className={styles.input}
              value={values.zip}
              onChange={handleChange("zip")}
            />
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            type="button"
            variant="highlightTertiary"
            className={styles.backButton}
            onClick={onBack}
            data-testid="onboarding-location-back"
          >
            {t("common.back")}
          </Button>
          <Button
            type="button"
            variant="primary"
            className={styles.finishButton}
            onClick={onFinish}
            data-testid="onboarding-location-finish"
          >
            {t("onboardingTour.locationStep.finish")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LocationDetailsStep;
